import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useBusinessType } from '@/hooks/useBusinessType';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  X, ChevronRight, ChevronLeft,
  LayoutDashboard, Users, PhoneCall, CalendarCheck, GraduationCap,
  Star, HelpCircle, Settings, Sparkles, CheckCircle,
  MessageSquare, Calendar, Upload, BarChart2, Kanban,
} from 'lucide-react';

import { API_URL } from '@/config/api';

const trainingSteps = [
  {
    id: 'welcome',
    title: 'Welcome to Sarum Digital!',
    content: 'Let\'s walk through your Training Hub so you know exactly where everything is. This takes about 2 minutes.',
    icon: Sparkles,
    position: 'center',
  },
  {
    id: 'dashboard',
    title: 'Your Dashboard',
    content: 'This is your home screen. See new students this week, upcoming registrations, today\'s classes, and quick action buttons to jump straight to what you need.',
    icon: LayoutDashboard,
    target: '[data-tour="nav-dashboard"]',
    position: 'right',
  },
  {
    id: 'students',
    title: 'Students',
    content: 'View all your students in one place. The "Registered" tab shows students who\'ve booked in but aren\'t on a course yet. "Enrolled" shows everyone currently studying. "Completed" shows graduates.',
    icon: Users,
    target: '[data-tour="nav-leads"]',
    position: 'right',
  },
  {
    id: 'followup',
    title: 'Follow-up Queue',
    content: 'Students the AI couldn\'t reach land here. You\'ll see why they need a call, how long they\'ve been waiting, and a big call button. Schedule callbacks right from the card.',
    icon: PhoneCall,
    target: '[data-tour="nav-manual-queue"]',
    position: 'right',
  },
  {
    id: 'calendar',
    title: 'Calendar',
    content: 'See your month at a glance. Pink dots are registrations, blue dots are course class days. Tap any day to see who\'s booked in and what classes are running. You can also add registrations manually.',
    icon: CalendarCheck,
    target: '[data-tour="nav-registrations"]',
    position: 'right',
  },
  {
    id: 'registration-complete',
    title: 'Completing a Registration',
    content: 'When a student turns up for registration, tap "Complete" on their card. You\'ll tick off eligibility and assessment checks, then enrol them straight into a course.',
    icon: CheckCircle,
    position: 'center',
  },
  {
    id: 'courses',
    title: 'Courses',
    content: 'Manage your courses here. Create new courses with dates, class days, and capacity. Use "Mark Register" on class days to take attendance. Students below 80% attendance get flagged.',
    icon: GraduationCap,
    target: '[data-tour="nav-courses"]',
    position: 'right',
  },
  {
    id: 'reviews',
    title: 'Reviews',
    content: 'When a student completes their course, request a Google review with one tap. This sends an automated message to the student asking them to leave a review.',
    icon: Star,
    target: '[data-tour="nav-reviews"]',
    position: 'right',
  },
  {
    id: 'support',
    title: 'Book Support',
    content: 'Need help? Book a 1-on-1 call with your account manager. You can also email us or check the FAQ.',
    icon: HelpCircle,
    target: '[data-tour="nav-support"]',
    position: 'right',
  },
  {
    id: 'settings',
    title: 'Settings',
    content: 'Update your business details, change your password, and configure integrations in Settings.',
    icon: Settings,
    target: '[data-tour="nav-settings"]',
    position: 'right',
  },
  {
    id: 'complete',
    title: 'You\'re all set!',
    content: 'That\'s everything! Start by checking your Dashboard for today\'s priorities. You can replay this tour anytime from Settings.',
    icon: Sparkles,
    position: 'center',
  },
];

const generalSteps = [
  {
    id: 'welcome',
    title: 'Welcome to Beluga Pro!',
    content: 'Let\'s take a quick tour to help you get started with managing your leads effectively.',
    icon: Sparkles,
    position: 'center',
  },
  {
    id: 'dashboard',
    title: 'Your Dashboard',
    content: 'This is your command center. See total leads, revenue, pipeline status, and recent activity at a glance.',
    icon: LayoutDashboard,
    target: '[data-tour="nav-dashboard"]',
    position: 'right',
  },
  {
    id: 'leads',
    title: 'Leads',
    content: 'View and manage all your leads. Search, filter by tags or status, and click any lead to see full details.',
    icon: Users,
    target: '[data-tour="nav-leads"]',
    position: 'right',
  },
  {
    id: 'pipeline',
    title: 'Pipeline View',
    content: 'The Pipeline page shows your leads in a Kanban board. Drag leads between stages to track progress.',
    icon: Kanban,
    target: '[data-tour="nav-pipeline"]',
    position: 'right',
  },
  {
    id: 'followups',
    title: 'Follow-ups',
    content: 'Never miss a callback! The Follow-ups page shows all leads with scheduled callbacks, sorted by date.',
    icon: Calendar,
    target: '[data-tour="nav-followups"]',
    position: 'right',
  },
  {
    id: 'chat-tab',
    title: 'Communicating with Leads',
    content: 'Open any lead and use the Chat Tab to send pre-written messages via WhatsApp, SMS, or Email.',
    icon: MessageSquare,
    position: 'center',
  },
  {
    id: 'help',
    title: 'Need Help?',
    content: 'Visit the Help page anytime for tutorials, FAQ, and video guides.',
    icon: HelpCircle,
    target: '[data-tour="nav-help"]',
    position: 'right',
  },
  {
    id: 'complete',
    title: 'You\'re All Set!',
    content: 'Start by adding your first lead or exploring the Pipeline. Good luck with your lead management!',
    icon: Sparkles,
    position: 'center',
  },
];

const OnboardingTour = ({ onComplete }) => {
  const { user } = useAuth();
  const { isTrainingCentre } = useBusinessType();
  const [currentStep, setCurrentStep] = useState(0);
  const [showTour, setShowTour] = useState(false);
  const [tourCompleted, setTourCompleted] = useState(false);

  const steps = isTrainingCentre ? trainingSteps : generalSteps;

  useEffect(() => {
    checkOnboardingStatus();
  }, []);

  const checkOnboardingStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/user/onboarding`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (!data.completed && data.show_tour !== false) {
          setTimeout(() => setShowTour(true), 1000);
        }
      }
    } catch (error) {
      console.error('Error checking onboarding status:', error);
    }
  };

  const updateOnboardingStatus = async (completed = false) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/user/onboarding?tour_completed=${completed}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error updating onboarding status:', error);
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    updateOnboardingStatus(true);
    setShowTour(false);
    setTourCompleted(true);
    if (onComplete) onComplete();
  };

  const handleComplete = () => {
    updateOnboardingStatus(true);
    setShowTour(false);
    setTourCompleted(true);
    if (onComplete) onComplete();
  };

  if (!showTour || tourCompleted) return null;

  const step = steps[currentStep];
  const progress = ((currentStep + 1) / steps.length) * 100;
  const StepIcon = step.icon || Sparkles;

  const gradientClass = isTrainingCentre
    ? 'from-pink-400 to-rose-500'
    : 'from-cyan-500 to-blue-600';

  const progressBarClass = isTrainingCentre
    ? 'from-pink-400 to-rose-500'
    : 'from-cyan-500 to-blue-600';

  const dotActiveClass = isTrainingCentre ? 'bg-pink-500' : 'bg-cyan-500';
  const dotDoneClass = isTrainingCentre ? 'bg-pink-200' : 'bg-cyan-200';

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 z-50" data-testid="onboarding-overlay" />

      {/* Tour Card */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4">
        <Card className="w-full max-w-sm sm:max-w-md shadow-2xl animate-in fade-in zoom-in duration-300 border-0">
          <CardContent className="p-4 sm:p-6">
            {/* Progress */}
            <div className="mb-3 sm:mb-4">
              <div className="flex items-center justify-between text-xs sm:text-sm text-slate-500 mb-2">
                <span>Step {currentStep + 1} of {steps.length}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSkip}
                  className="text-slate-400 hover:text-slate-600 text-xs sm:text-sm h-8 px-2"
                >
                  Skip tour
                </Button>
              </div>
              <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={`h-full bg-gradient-to-r ${progressBarClass} transition-all duration-300`}
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {/* Icon */}
            <div className="flex justify-center mb-3 sm:mb-4">
              <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-r ${gradientClass} flex items-center justify-center shadow-lg`}>
                <StepIcon className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
              </div>
            </div>

            {/* Content */}
            <div className="text-center mb-4 sm:mb-6">
              <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {step.title}
              </h3>
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{step.content}</p>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                onClick={handlePrev}
                disabled={currentStep === 0}
                className="gap-1 text-xs sm:text-sm min-h-[44px] px-3 rounded-xl"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </Button>

              <div className="flex gap-1 max-w-[100px] sm:max-w-none overflow-hidden">
                {steps.slice(Math.max(0, currentStep - 3), Math.min(steps.length, currentStep + 4)).map((_, i) => {
                  const actualIndex = Math.max(0, currentStep - 3) + i;
                  return (
                    <div
                      key={actualIndex}
                      className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full transition-colors flex-shrink-0 ${
                        actualIndex === currentStep
                          ? dotActiveClass
                          : actualIndex < currentStep
                            ? dotDoneClass
                            : 'bg-slate-200'
                      }`}
                    />
                  );
                })}
              </div>

              <Button
                onClick={handleNext}
                className={`gap-1 bg-gradient-to-r ${gradientClass} text-white text-xs sm:text-sm min-h-[44px] px-4 rounded-xl shadow-md`}
              >
                {currentStep === steps.length - 1 ? (
                  <>Get Started <CheckCircle className="w-4 h-4" /></>
                ) : (
                  <>Next <ChevronRight className="w-4 h-4" /></>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

// Contextual Help Tooltip Component
export const HelpTooltip = ({ content, children }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative inline-flex items-center">
      {children}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="ml-1 text-slate-400 hover:text-cyan-500 transition-colors"
        aria-label="Help"
      >
        <HelpCircle className="w-4 h-4" />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 w-64 p-3 bg-slate-900 text-white text-sm rounded-lg shadow-xl">
            <button
              onClick={() => setIsOpen(false)}
              className="absolute top-2 right-2 text-slate-400 hover:text-white"
            >
              <X className="w-3 h-3" />
            </button>
            {content}
            <div className="absolute left-0 top-1/2 -translate-x-full -translate-y-1/2">
              <div className="border-8 border-transparent border-r-slate-900" />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OnboardingTour;
